import { graphHooks } from '@tunasong/graph-lib/react'
import { useEditor } from '@tunasong/plugin-lib'
import type { Comment, Entity, Persisted } from '@tunasong/schemas'
import type { FC } from 'react'
import { useState } from 'react'
import { ProposalItem } from './proposal-item.js'
import {
  acceptProposal,
  rejectProposal,
  type ProposalComment,
} from './proposal-resolve.js'
import { useAgentProposals } from './use-agent-proposals.js'
import styles from './proposals-header.module.css'

export interface ProposalsHeaderProps {
  /** Agent whose pending proposals are surfaced in this header. */
  agentId: string
  /** Current user id, stamped onto resolutions. */
  userId?: string
  /**
   * Force an expanded state. When omitted, the header auto-expands for a
   * single pending proposal (one row is cheap; Recognition over Recall)
   * and stays collapsed for 2+ proposals so the chat below isn't buried.
   */
  defaultExpanded?: boolean
}

/**
 * Sticky `Proposals (n)` strip above the chat in the agent side panel.
 * Renders nothing when the agent has no pending proposals.
 *
 * Click the toggle → expand a scrollable list of `ProposalItem`s, each
 * with Accept/Reject. Shares the `proposal-resolve` accept/reject path
 * with the gutter chip popover — single audit trail.
 */
export const ProposalsHeader: FC<ProposalsHeaderProps> = ({
  agentId,
  userId,
  defaultExpanded,
}) => {
  // The proposals strip is mounted inside `EntityChrome`'s `TunaEditorContext`
  // provider when shown in the sidebar — we read the editor from context so
  // callers don't need to thread it as a prop. `allowNull: true` keeps the
  // component safe to render in tests / surfaces without an editor context
  // (e.g. archived entity views); the accept path treats `null` as "no
  // slate-op patches" via `proposal-resolve`.
  const editor = useEditor({ allowNull: true })
  const proposals = useAgentProposals(agentId)
  const updateComment = graphHooks.useEntityUpdate<Persisted<Comment>>({})
  const updateEntity = graphHooks.useEntityUpdate<Persisted<Entity>>({})
  // Auto-expand when N=1 (cheap, no scroll, single-glance) and collapse
  // for 2+ so the chat isn't buried. Caller can override.
  const initialExpanded = defaultExpanded ?? proposals.length === 1
  const [expanded, setExpanded] = useState(initialExpanded)
  const [errors, setErrors] = useState<Record<string, string>>({})

  if (proposals.length === 0) {
    return null
  }

  const resolveCtx = { editor, userId, updateComment, updateEntity }
  const handleAccept = (proposal: ProposalComment) => () => {
    const result = acceptProposal(proposal, resolveCtx)
    setErrors(prev => {
      const next = { ...prev }
      if (result.ok) {
        delete next[proposal.id]
      } else {
        next[proposal.id] = result.reason
      }
      return next
    })
  }
  const handleReject = (proposal: ProposalComment) => () => {
    rejectProposal(proposal, resolveCtx)
  }

  return (
    <div className={styles.header}>
      <button
        type="button"
        className={styles.toggle}
        aria-expanded={expanded}
        onClick={() => setExpanded(prev => !prev)}
      >
        <span>Proposals ({proposals.length})</span>
        <span className={styles.caret} data-expanded={expanded ? 'true' : undefined} aria-hidden="true" />
      </button>
      {expanded ? (
        <div role="list" className={styles.list}>
          {proposals.map(proposal => (
            <ProposalItem
              key={proposal.id}
              proposal={proposal}
              hideTargetLabel
              error={errors[proposal.id]}
              onAccept={handleAccept(proposal)}
              onReject={handleReject(proposal)}
            />
          ))}
        </div>
      ) : null}
    </div>
  )
}
